import React, { useEffect, useState } from 'react';
import { DocumentViewer, ProviderDocumentUrl, SupportDocumentUrl, renderDate, Spinner } from '../../../..';
import { IFaxDetailProps } from './fax-detail.types';
import * as S from '../claim-timeline.styles';
import * as Card from './timeline-detail-card.styles';
import ContentLoader from 'react-content-loader';

const LinePlaceholder = React.memo(() => {
    return (
        <ContentLoader speed={2} height={20} style={{ width: '100%' }} preserveAspectRatio="">
            <rect x="0" y="0" rx="0" ry="0" width="50%" height="20" />
        </ContentLoader>
    );
});

export const FaxDetail: React.FunctionComponent<IFaxDetailProps> = (
    props: IFaxDetailProps
): React.ReactElement => {
    const { fax, appType, downloadDocument } = props;
    const [loading, setLoading] = useState(false);
    const [documentUrl, setDocumentUrl] = useState<SupportDocumentUrl | ProviderDocumentUrl | undefined>();

    useEffect(() => {
        (async (): Promise<void> => {
            const docId = appType === 'support' ? fax.documentId : fax.document?.id;
            const fetchId = appType === 'support' ? docId : fax.id;
            
            if (fetchId) {
                setLoading(true);
                try {
                    const url = await downloadDocument(fetchId);
                    setDocumentUrl(url);
                } finally {
                    setLoading(false);
                }
            }
        })();
    }, [fax, appType, downloadDocument]);

    return (
        <Card.CdPanel $embedded key={fax.id}>
            <Card.CdSectionCard $embedded>
                <S.ExpandTable>
                    <S.ExpandTBody>
                        {appType === 'support' ? (
                            <>
                                <S.ExpandTR>
                                    <S.ExpandTH>ID</S.ExpandTH>
                                    <S.ExpandTD>{fax.id}</S.ExpandTD>
                                </S.ExpandTR>
                                <S.ExpandTR>
                                    <S.ExpandTH>External ID</S.ExpandTH>
                                    <S.ExpandTD>{fax.externalId}</S.ExpandTD>
                                </S.ExpandTR>
                                <S.ExpandTR>
                                    <S.ExpandTH>Result</S.ExpandTH>
                                    <S.ExpandTD>{fax.result}</S.ExpandTD>
                                </S.ExpandTR>
                                <S.ExpandTR>
                                    <S.ExpandTH>Sender phone</S.ExpandTH>
                                    <S.ExpandTD>{fax.fromE164}</S.ExpandTD>
                                </S.ExpandTR>
                                <S.ExpandTR>
                                    <S.ExpandTH>Receiver phone</S.ExpandTH>
                                    <S.ExpandTD>{fax.toE164}</S.ExpandTD>
                                </S.ExpandTR>
                                <S.ExpandTR>
                                    <S.ExpandTH>Pages count</S.ExpandTH>
                                    <S.ExpandTD>{fax.pages}</S.ExpandTD>
                                </S.ExpandTR>
                                <S.ExpandTR>
                                    <S.ExpandTH>Send Date</S.ExpandTH>
                                    <S.ExpandTD>{renderDate(fax.sent)}</S.ExpandTD>
                                </S.ExpandTR>
                                <S.ExpandTR>
                                    <S.ExpandTH>Created Date</S.ExpandTH>
                                    <S.ExpandTD>{renderDate(fax.created)}</S.ExpandTD>
                                </S.ExpandTR>
                            </>
                        ) : (
                            <>
                                <S.ExpandTR>
                                    <S.ExpandTH>To</S.ExpandTH>
                                    <S.ExpandTD>{fax.toE164}</S.ExpandTD>
                                </S.ExpandTR>
                                <S.ExpandTR>
                                    <S.ExpandTH># of Pages</S.ExpandTH>
                                    <S.ExpandTD>{fax.pages}</S.ExpandTD>
                                </S.ExpandTR>
                                <S.ExpandTR>
                                    <S.ExpandTH>Attempts</S.ExpandTH>
                                    <S.ExpandTD>{fax.attempts}</S.ExpandTD>
                                </S.ExpandTR>
                                <S.ExpandTR>
                                    <S.ExpandTH>Sent</S.ExpandTH>
                                    <S.ExpandTD>{renderDate(fax.sent)}</S.ExpandTD>
                                </S.ExpandTR>
                            </>
                        )}
                    </S.ExpandTBody>
                </S.ExpandTable>
            </Card.CdSectionCard>
            {(loading || documentUrl?.url) && (
                <Card.CdSectionCard $embedded>
                    {loading && (appType === 'support' ? <LinePlaceholder /> : <Spinner />)}
                    {documentUrl?.url && <DocumentViewer url={documentUrl.url} />}
                </Card.CdSectionCard>
            )}
        </Card.CdPanel>
    );
};
